import React, { useState } from 'react';
import { Table, Switch, Button } from 'antd';
import { CampaignMemberDto } from '@api/Api';
import { OrganizationRoleCopy, WorkspaceRoleCopy } from '@helpers/userRoles';
import { useTypedSelector } from '@hooks';
import { ActionToast } from '@components/Toasts';
import { toast } from 'react-toastify';
import UserAvatar from '@components/UserAvatar';
import { useOrganization } from '@components/OrganizationBoundary';

function CampaignMembersTable({
  loading,
  userList,
  usersCount,
  onChangeOrderBy,
  onShareAccess,
  onRevokeAssess
}: {
  loading: boolean;
  userList: CampaignMemberDto[];
  usersCount: number;
  onChangeOrderBy: (a: string) => void;
  onShareAccess: (a: string) => void;
  onRevokeAssess: (a: string, b: boolean) => void;
}) {
  const { currentOrganization } = useOrganization();
  const [switchLoading, setSwitchLoading] = useState<string | false>(false);
  const campaignInfoModal = useTypedSelector(
    ({ modal }) => modal.campaignInfoModal
  );
  const columns = [
    {
      title: () => {
        return (
          <>
            User <span className="users_count">{usersCount}</span>
          </>
        );
      },
      dataIndex: 'name',
      key: 'name',
      render: (text: string, node: CampaignMemberDto) => {
        return (
          <div className="user_record">
            <div className="user_record__avatar">
              <UserAvatar
                isActive={!node.invitationEmail}
                size={40}
                src={node.user?.picture.url || ''}
                userEmail={node.user?.email || ''}
                userName={node.user?.name || ''}
              />
            </div>
            <div className="user_record__info">
              <div className="name">{node.user?.name}</div>
              <div className="email main-body-text main-body-text--tiny main-body-text--dark-gray">
                {node.user?.email || node.invitationEmail}
                {node.invitationEmail && (
                  <Button
                    className="email-resend"
                    type="link"
                    size="small"
                    disabled={
                      currentOrganization.seatsCount.count >=
                      (currentOrganization.entity?.limits?.orgMembers ??
                        Number.POSITIVE_INFINITY)
                    }
                    onClick={async () => {
                      await onShareAccess(node.id);
                      toast(
                        <ActionToast
                          title="Invite sent!"
                          closeToast={undefined}
                          onUndo={undefined}
                          description={undefined}
                        />,
                        {
                          hideProgressBar: true,
                          delay: 222,
                          style: { width: '345px' },
                          bodyClassName: 'toast_container--invitation'
                        }
                      );
                    }}
                  >
                    Resend
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      },
      width: 324
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      render: (text: string, node: CampaignMemberDto) => {
        const isAdmin = node.organizationRole === 'organization://admin';
        const role =
          node.workspaceRole && !isAdmin
            ? WorkspaceRoleCopy[node.workspaceRole]
            : OrganizationRoleCopy[node.organizationRole];
        return role;
      },

      width: 324
    },
    {
      title: () =>
        campaignInfoModal?.campaign?.isPrivate && (
          <span style={{ float: 'right' }}>Access to campaign</span>
        ),
      key: 'access_to_campaign',
      dataIndex: 'access_to_campaign',
      render: (text: string, node: CampaignMemberDto) => {
        if (
          node?.accessControl === 'add_delete' &&
          node?.permissions.revokeAccess
        ) {
          return (
            <Button
              type="link"
              onClick={async () => {
                setSwitchLoading(node.id);
                await onRevokeAssess(node.id, true);
                setSwitchLoading(false);
              }}
              loading={switchLoading === node.id}
              disabled={!node.permissions.revokeAccess}
              className="main-body-text main-body-text--semibold main-body-text--dark-gray"
              style={{ float: 'right', padding: 0, height: 40 }}
            >
              Remove
            </Button>
          );
        }
        let isSwitcherDisabled = false;
        if (node.hasAccessToPrivate && !node.permissions.revokeAccess)
          isSwitcherDisabled = true;

        if (!node.hasAccessToPrivate && !node.permissions.shareAccess)
          isSwitcherDisabled = true;
        return (
          <div style={{ float: 'right' }}>
            {campaignInfoModal?.campaign?.isPrivate &&
              node.accessControl === 'enable_disable' && (
                <>
                  <Switch
                    onChange={async () => {
                      setSwitchLoading(node.id);
                      if (!node.hasAccessToPrivate)
                        await onShareAccess(node.id);
                      else await onRevokeAssess(node.id, false);
                      setSwitchLoading(false);
                    }}
                    disabled={isSwitcherDisabled}
                    loading={switchLoading === node.id}
                    checked={node.hasAccessToPrivate}
                  />
                  <span style={{ marginLeft: 12 }}>
                    {node.hasAccessToPrivate ? 'On' : 'Off'}
                  </span>
                </>
              )}
          </div>
        );
      },
      width: 324
    }
  ];
  return (
    <Table
      loading={loading}
      pagination={false}
      columns={columns}
      dataSource={userList}
      rowKey={(node) => node.id}
      onChange={(pagination, filters, sorter: any) => {
        let sortOrder = 'default:ASC';
        if (sorter.order === 'descend') sortOrder = 'role:DESC';
        else if (sorter.order === 'ascend') sortOrder = 'role:ASC';

        onChangeOrderBy(sortOrder);
      }}
    />
  );
}

export default CampaignMembersTable;
